/**
 * Reader text helpers: times in the profile's time zone (the prototype shows "09:42" in rows and
 * "Today · 09:42" in the reader), score titles and the reading-time footer.
 */

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string | undefined, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${timeZone ?? ''}|${JSON.stringify(options)}`;
  let f = formatters.get(key);
  if (!f) {
    try {
      f = new Intl.DateTimeFormat('en-GB', { ...options, ...(timeZone ? { timeZone } : {}) });
    } catch {
      f = new Intl.DateTimeFormat('en-GB', options);
    }
    formatters.set(key, f);
  }
  return f;
}

const dayKey = (at: number, timeZone: string | undefined): string => formatter(timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);

/** "09:42". */
export function clockTime(at: number, timeZone?: string): string {
  return formatter(timeZone, { hour: '2-digit', minute: '2-digit', hour12: false }).format(at);
}

/** "Today", "Yesterday", "3 Oct" or "3 Oct 2025". */
export function dayLabel(at: number, timeZone?: string, now: number = Date.now()): string {
  const day = dayKey(at, timeZone);
  if (day === dayKey(now, timeZone)) return 'Today';
  if (day === dayKey(now - 86_400_000, timeZone)) return 'Yesterday';
  const sameYear = formatter(timeZone, { year: 'numeric' }).format(at) === formatter(timeZone, { year: 'numeric' }).format(now);
  return formatter(timeZone, sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' }).format(at);
}

/** A row's time: the clock for today, the day otherwise. */
export function rowTime(at: number, timeZone?: string, now: number = Date.now()): string {
  const day = dayLabel(at, timeZone, now);
  return day === 'Today' ? clockTime(at, timeZone) : day;
}

/** The reader's "Today · 09:42". */
export function readerTime(at: number, timeZone?: string, now: number = Date.now()): string {
  return `${dayLabel(at, timeZone, now)} · ${clockTime(at, timeZone)}`;
}

/** Timeline meta: the clock for today, "3 Oct · 09:42" otherwise. */
export function eventTime(at: number, timeZone?: string, now: number = Date.now()): string {
  const day = dayLabel(at, timeZone, now);
  return day === 'Today' ? clockTime(at, timeZone) : `${day} · ${clockTime(at, timeZone)}`;
}

/** "mm:ss" or "h:mm:ss" for transcript time marks. */
export function timeMark(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${String(m).padStart(2, '0')}:${sec}`;
}

/**
 * The heading of a message without a title (a toot, a Telegram post, a Reddit comment): its first
 * sentence when that is short, else the excerpt cut at a word near `max` characters with an ellipsis —
 * never the whole excerpt as a giant heading.
 */
export function headingOf(row: { title: string | null; excerpt: string | null }, max = 110): string {
  if (row.title) return row.title;
  const text = (row.excerpt ?? '').replace(/\s+/g, ' ').trim();
  if (text.length <= max) return text;
  const sentence = /^(.{20,}?[.!?…])\s/.exec(text);
  if (sentence?.[1] && sentence[1].length <= max) return sentence[1];
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).replace(/[\s,;:–—-]+$/, '')}…`;
}
